import { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react'
import Cookies from 'js-cookie'
import { Request, REQUEST_MESSAGE_TYPES } from '../schemas/request'
import { useDispatch } from '../redux/store'
import { useSnackbar, SnackbarKey } from 'notistack'
import { setPingTimeout, updatePingInfo } from '../redux/slices/socket-health'
import { generateID } from '../utils/generateMessageID'
import addMessageChunks from '../utils/messageSequence'
import { eventCentral } from '../redux/action-creators/event-central'
import { ConfigContext } from './ConfigurationContext'
import { setAuthenticated } from '../redux/slices/socket-auth-status'
import { isJwtValid } from 'utils/jwt'

type SocketContextType = {
  onSocketSend: (msg: Request) => Promise<boolean>
  onSocketClosed: () => void
  socket: React.MutableRefObject<WebSocket | null>
  reconnectSocket: () => void
  checkWebSocketStatus: () => string
  isAuthenticated: boolean
  getLastPingTime: React.MutableRefObject<number>
  waitForAuthentication: () => Promise<void>
}

export const SocketContext = createContext<SocketContextType | null>(null)

// ✅ Singleton WebSocket container
const staticSocket = {
  instance: null as WebSocket | null,
  initialized: false
}

export const SocketProvider = ({ children }) => {
  const dispatch = useDispatch()
  const config = useContext(ConfigContext)
  const { enqueueSnackbar, closeSnackbar } = useSnackbar()

  const socket = useRef<WebSocket | null>(null)
  const lastPingTime = useRef(Date.now())
  const pingInterval = useRef(parseInt(config?.REACT_APP_PING_INTERVAL_IN_MS || '700000'))
  const reconnecting = useRef(false)
  const notificationKey = useRef<SnackbarKey | null>(null)
  const decodedChunks = useRef<Record<string, string>>({})
  const [isAuthenticated, setIsAuthenticated] = useState(false)

  const reconnectTimeoutId = useRef<NodeJS.Timeout | null>(null)

  const authCallbackRef = useRef<() => void>(() => {})
  const hasConfirmedAuth = useRef(false)

  const handleTokenExpiry = () => {
    Cookies.remove('token')

    if (socket.current?.readyState === WebSocket.OPEN) {
      socket.current.close()
    }

    reconnecting.current = false
    staticSocket.initialized = false
    hasConfirmedAuth.current = false
    setIsAuthenticated(false)
    dispatch(setAuthenticated(false))

    window.location.href = '/auth/login' // or navigate('/auth/login') if in component
  }

  function setupSocket(ws: WebSocket) {
    ws.onopen = () => {
      // ✅ Reset reconnection attempts on successful connection
      reconnectAttempt.current = 0
      reconnecting.current = false
      lastPingTime.current = Date.now()
      dispatch(updatePingInfo({ lastPingTime: lastPingTime.current, pingLatency: 0 }))

      // 🔍 Table logging WebSocket status
      console.table({
        Time: new Date().toLocaleTimeString(),
        'Socket State': ws.readyState,
        'Static Initialized': staticSocket.initialized,
        'Has Token': !!Cookies.get('token'),
        Reconnecting: reconnecting.current,
        'Has Confirmed Auth': hasConfirmedAuth.current
      })

      if (notificationKey.current) {
        closeSnackbar(notificationKey.current)
        notificationKey.current = null
      }
  
      const token = Cookies.get('token')
      if (token && ws.readyState === WebSocket.OPEN) {
        const msg: Request = {
          messageId: generateID(),
          messageType: REQUEST_MESSAGE_TYPES.Auth,
          Auth: { jwt: token }
        }
        ws.send(JSON.stringify(msg))
      }
    }
  
    ws.onmessage = (event: MessageEvent) => {
      const now = Date.now()
      lastPingTime.current = now
      dispatch(updatePingInfo({ lastPingTime: now, pingLatency: 0 }))
  
      let data
      try {
        data = JSON.parse(event.data)
      } catch {
        return
      }

      // ✅ Handle token expiry before anything else
      if (
        data.messageType === 'Status' &&
        (data.Status?.statusCode === 9777 || data.Status?.status === 'TimedOut')
      ) {
        handleTokenExpiry()
        return
      }

      switch (data.messageType) {
        case 'AuthConfirmation':
          setIsAuthenticated(true)
          dispatch(setAuthenticated(true))
          hasConfirmedAuth.current = true
          eventCentral(event.data)
  
          if (authCallbackRef.current) {
            authCallbackRef.current()
          }
          break
  
        case 'MessageSegment32KB': {
          const ref = data.messageIdReference
          decodedChunks.current[ref] = addMessageChunks(data, decodedChunks.current[ref])
          if (
            data?.MessageSegment32KB?.part === data?.MessageSegment32KB?.parts &&
            decodedChunks.current[ref]
          ) {
            eventCentral(decodedChunks.current[ref])
          }
          break
        }
  
        default:
          eventCentral(event.data)
      }
    }
  
    ws.onclose = () => reconnectSocket()
    ws.onerror = () => reconnectSocket()
  }

  const waitForAuthentication = () => {
    return new Promise<void>((resolve) => {
      if (hasConfirmedAuth.current) {
        resolve()
      } else {
        authCallbackRef.current = () => {
          resolve()
        }
      }
    })
  }

  // ✅ Exponential backoff for reconnection attempts
  const reconnectAttempt = useRef(0)
  const maxReconnectAttempts = 5
  const baseDelay = 1000 // 1 second base delay

  const getReconnectDelay = () => {
    // Exponential backoff: 1s, 2s, 4s, 8s, 16s
    return Math.min(baseDelay * Math.pow(2, reconnectAttempt.current), 30000)
  }

  const reconnectSocket = useCallback(() => {
    const state = socket.current?.readyState
    if (reconnecting.current || state === WebSocket.CONNECTING || state === WebSocket.OPEN) {
      return
    }

    // Check network connectivity before attempting reconnect
    if (!navigator.onLine) {
      console.log('📡 Network offline - skipping reconnect attempt')
      return
    }

    reconnecting.current = true
    reconnectAttempt.current += 1

    if (reconnectTimeoutId.current) {
      return
    }

    if (notificationKey.current) closeSnackbar(notificationKey.current)

    const delay = getReconnectDelay()
    const attemptNumber = reconnectAttempt.current

    const key = enqueueSnackbar(
      `Connection lost. Reconnecting (attempt ${attemptNumber}/${maxReconnectAttempts})...`,
      {
        variant: 'warning',
        persist: true
      }
    )
    notificationKey.current = key

    console.log(
      `🔄 Reconnect attempt ${attemptNumber} (waiting ${delay}ms)`,
      `Max attempts: ${maxReconnectAttempts}`
    )

    reconnectTimeoutId.current = setTimeout(async () => {
      const token = Cookies.get('token')
      const currentSocket = socket.current

      if (currentSocket?.readyState === WebSocket.OPEN && token) {
        const msg: Request = {
          messageId: generateID(),
          messageType: REQUEST_MESSAGE_TYPES.DeAuth,
          DeAuth: { jwt: token }
        }
        currentSocket.send(JSON.stringify(msg))
        currentSocket.close()
      }

      if (reconnectAttempt.current > maxReconnectAttempts) {
        console.error('❌ Max reconnection attempts reached')
        if (notificationKey.current) {
          closeSnackbar(notificationKey.current)
          enqueueSnackbar('Failed to reconnect. Please refresh the page.', {
            variant: 'error',
            persist: true
          })
        }
        reconnecting.current = false
        return
      }

      const url = currentSocket?.url || config.REACT_APP_AWS_SOCKET
      try {
        const newSocket = new WebSocket(url)
        socket.current = newSocket
        staticSocket.instance = newSocket
        staticSocket.initialized = true
        reconnectTimeoutId.current = null
        setupSocket(newSocket)
      } catch (error) {
        console.error('❌ WebSocket creation error:', error)
        reconnecting.current = false
        reconnectSocket() // Retry again
      }
    }, delay)
  }, [config.REACT_APP_AWS_SOCKET, enqueueSnackbar, closeSnackbar])

  const onSocketSend = async (msg: Request) => {
    await waitForAuthentication()
    if (!socket.current || socket.current.readyState !== WebSocket.OPEN) {
      reconnectSocket()
      return false
    }

    socket.current.send(JSON.stringify(msg))
    return true
  }

  const onSocketClosed = () => {
    const token = Cookies.get('token')
    if (socket.current?.readyState === WebSocket.OPEN && token) {
      const msg: Request = {
        messageId: generateID(),
        messageType: REQUEST_MESSAGE_TYPES.DeAuth,
        DeAuth: { jwt: token }
      }
      onSocketSend(msg).then(() => {
        Cookies.remove('token')
        socket.current?.close()
      })
    }
  }


  
  const checkWebSocketStatus = () => {
    return ['Connecting', 'Open', 'Closing', 'Closed'][socket.current?.readyState || 3]
  }

  useEffect(() => {
    if (staticSocket.initialized) return

    let checkInterval: NodeJS.Timeout

    const tryInit = () => {
      const token = Cookies.get('token')
      if (!token) {
        return
      }

      if (!isJwtValid()) {
        return
      }

      if (socket.current?.readyState === WebSocket.CONNECTING) {
        return
      }

      if (socket.current?.readyState === WebSocket.OPEN) {
        return
      }

      const ws = new WebSocket(config?.REACT_APP_AWS_SOCKET)
      socket.current = ws
      staticSocket.instance = ws
      staticSocket.initialized = true
      setupSocket(ws)
      clearInterval(checkInterval)
    }

    checkInterval = setInterval(tryInit, 500)

    return () => {
      clearInterval(checkInterval)
      if (reconnectTimeoutId.current) {
        clearTimeout(reconnectTimeoutId.current)
        reconnectTimeoutId.current = null
      }
    }
  }, [setupSocket, config?.REACT_APP_AWS_SOCKET])
  

  // 🕑 Centralized ping monitor
  useEffect(() => {
    const id = setInterval(() => {
      const now = Date.now()
      const diff = now - lastPingTime.current

      dispatch(updatePingInfo({ lastPingTime: lastPingTime.current, pingLatency: diff }))

      if (diff > pingInterval.current) {
        dispatch(setPingTimeout())
        socket.current?.close()
        reconnectSocket()
      }
    }, 5000)

    return () => clearInterval(id)
  }, [dispatch, reconnectSocket])

  // 📡 Network connectivity monitoring
  useEffect(() => {
    const handleOnline = () => {
      console.log('🟢 Network online detected')
      if (socket.current?.readyState !== WebSocket.OPEN) {
        reconnectAttempt.current = 0 // Reset reconnect attempts on network recovery
        reconnectSocket()
      }
    }

    const handleOffline = () => {
      console.log('🔴 Network offline detected')
      if (socket.current?.readyState === WebSocket.OPEN) {
        socket.current.close()
      }
    }

    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)

    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
    }
  }, [reconnectSocket])

  return (
    <SocketContext.Provider
      value={{
        onSocketSend,
        onSocketClosed,
        socket,
        reconnectSocket,
        checkWebSocketStatus,
        isAuthenticated,
        getLastPingTime: lastPingTime,
        waitForAuthentication
      }}
    >
      {children}
    </SocketContext.Provider>
  )
}